import { __assign, __awaiter, __extends, __generator } from "tslib";
import { values, keyBy, merge } from 'lodash';
import Model from './model';
import { TaskStatuses, TaskCodes, VisitStatuses, COMPLETE_VISIT_STATUSES } from '@goodrx/heydoctor-core';
import { Tasks } from '../constants';
export var DEFAULT_TASK = Tasks.find(function (t) { return t.code === 'Summary'; });
export var getTaskSlug = function (task) { var _a; return (_a = Tasks.find(function (t) { return t.code === task.code; })) === null || _a === void 0 ? void 0 : _a.slug; };
var BASE_URL = '/visits';
var Visit = (function (_super) {
    __extends(Visit, _super);
    function Visit(data, client) {
        var _this = _super.call(this, data, client) || this;
        _this.data = data;
        _this.client = client;
        _this.isVisitComplete = function () {
            return _this.data.orderedTasks.length > 0 && _this.data.orderedTasks.every(function (t) { return t.status === TaskStatuses.COMPLETE; });
        };
        _this.isVisitCancelled = function () { return _this.data.status === VisitStatuses.CANCELLED; };
        _this.isVisitAbandoned = function () { return _this.data.status === VisitStatuses.ABANDONED; };
        _this.getTaskBySlug = function (slug) { return _this.data.orderedTasks.find(function (t) { return getTaskSlug(t) === slug; }) || DEFAULT_TASK; };
        _this.getChargeAmount = function () {
            var promotion = _this.data.promotion;
            var baseCost = _this.getBaseCost();
            if (!promotion) {
                return baseCost;
            }
            return _this.getDiscountedCost(baseCost, promotion.discountType, promotion.discountAmount);
        };
        _this.data.tasks = keyBy(_this.data.tasks, 'code');
        _this.setOrderedTasks();
        return _this;
    }
    Object.defineProperty(Visit.prototype, "orderedTasks", {
        get: function () {
            return this.data.orderedTasks;
        },
        enumerable: false,
        configurable: true
    });
    Visit.prototype.isComplete = function () {
        return COMPLETE_VISIT_STATUSES.includes(this.data.status);
    };
    Visit.prototype.isTaskComplete = function (taskCode) {
        var task = this.data.orderedTasks.find(function (t) { return t.code === taskCode; });
        return task && task.status === TaskStatuses.COMPLETE;
    };
    Visit.prototype.updateTask = function (code, updates) {
        var _a;
        var visit = this.createSelf({
            tasks: (_a = {},
                _a[code] = merge({}, this.tasks[code], updates),
                _a),
        });
        this.setOrderedTasks();
        return visit;
    };
    Visit.prototype.completeTask = function (code) {
        return this.updateTask(code, {
            status: TaskStatuses.COMPLETE,
        });
    };
    Visit.prototype.setOrderedTasks = function () {
        this.data.orderedTasks = values(this.data.tasks).sort(function (a, b) { return (a.priority < b.priority ? -1 : 1); });
    };
    Visit.prototype.getNextTask = function () {
        var tasks = this.data.orderedTasks || [];
        if ([VisitStatuses.CANCELLED, VisitStatuses.ABANDONED].includes(this.data.status)) {
            return DEFAULT_TASK;
        }
        var nextTask = tasks.find(function (t) { return t.status === TaskStatuses.OPEN || t.status === TaskStatuses.REJECTED; });
        return nextTask || DEFAULT_TASK;
    };
    Visit.prototype.getDiscountedCost = function (baseCost, discountType, discountAmount) {
        switch (discountType) {
            case 'percent':
                return Math.max(0, baseCost - (baseCost * discountAmount) / 100);
            case 'dollar':
                return Math.max(0, baseCost - discountAmount);
            default:
                return baseCost;
        }
    };
    Visit.prototype.getBaseCost = function () {
        return this.data.service.retailCost / 100 + this.data.service.fixedCost / 100;
    };
    Visit.prototype.postConsent = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/consent")];
                    case 1:
                        _a.sent();
                        return [2, this.completeTask(TaskCodes.CONSENT)];
                }
            });
        });
    };
    Visit.prototype.consentToPhone = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/consent-phone")];
                    case 1:
                        _a.sent();
                        return [2, this.completeTask(TaskCodes.PHONE)];
                }
            });
        });
    };
    Visit.prototype.fetchQuestionnaire = function () {
        return __awaiter(this, void 0, void 0, function () {
            var questionnaire;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.get(BASE_URL + "/" + this.data.id + "/questionnaire")];
                    case 1:
                        questionnaire = (_a.sent()).data.data;
                        return [2, questionnaire];
                }
            });
        });
    };
    Visit.prototype.postAnswers = function (answers) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/questionnaire", { answers: answers })];
                    case 1:
                        _a.sent();
                        return [2, this.completeTask(TaskCodes.QUESTIONNAIRE)];
                }
            });
        });
    };
    Visit.prototype.postPhotoFormData = function (photoId, photo, onUploadProgress) {
        return __awaiter(this, void 0, void 0, function () {
            var formData, file;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        formData = new FormData();
                        formData.append('photo_id', photoId.toString());
                        formData.append('file', photo);
                        return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/photos", formData, {
                                headers: {
                                    'Content-Type': 'multipart/form-data',
                                },
                                timeout: 30000,
                                onUploadProgress: function (progressEvent) {
                                    var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                    onUploadProgress && onUploadProgress(percentCompleted);
                                },
                            })];
                    case 1:
                        file = (_a.sent()).data.data;
                        return [2, this.handlePostPhotoUpload(photoId, file.id)];
                }
            });
        });
    };
    Visit.prototype.postPhotoBase64 = function (photoId, photoData, onUploadProgress) {
        return __awaiter(this, void 0, void 0, function () {
            var file;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/photos/" + photoId, {
                            photo: photoData,
                        }, {
                            timeout: 30000,
                            onUploadProgress: function (progressEvent) {
                                var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                onUploadProgress && onUploadProgress(percentCompleted);
                            },
                        })];
                    case 1:
                        file = (_a.sent()).data.data;
                        return [2, this.handlePostPhotoUpload(photoId, file.id)];
                }
            });
        });
    };
    Visit.prototype.handlePostPhotoUpload = function (photoId, fileId) {
        var photos = this.data.photos.map(function (photo) {
            return photo.id === photoId ? __assign(__assign({}, photo), { status: TaskStatuses.UPLOADED, fileId: fileId }) : photo;
        });
        var hasUntakenRequiredPhotos = photos.some(function (photo) {
            return !!photo.required && photo.status !== TaskStatuses.UPLOADED;
        });
        return this.updateTask(TaskCodes.PHOTO, {
            status: hasUntakenRequiredPhotos ? TaskStatuses.OPEN : TaskStatuses.COMPLETE,
        }).createSelf({ photos: photos });
    };
    Visit.prototype.postPharmacy = function (type, pharmacy, deliveryAddress) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/delivery", {
                            type: type,
                            pharmacyId: pharmacy.id,
                            deliveryAddress: deliveryAddress,
                        })];
                    case 1:
                        _a.sent();
                        return [2, this.completeTask(TaskCodes.DELIVERY).createSelf({
                                delivery: {
                                    type: type,
                                    pharmacyAddress: __assign({}, pharmacy),
                                    deliveryAddress: type === 'mail_order'
                                        ? deliveryAddress
                                            ? __assign({ name: deliveryAddress.firstName + " " + deliveryAddress.lastName }, deliveryAddress) : {}
                                        : null,
                                },
                            })];
                }
            });
        });
    };
    Visit.prototype.applyPromotion = function (code) {
        return __awaiter(this, void 0, void 0, function () {
            var promotion;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/promotion", { code: code })];
                    case 1:
                        promotion = (_a.sent()).data.data;
                        return [2, this.createSelf({
                                promotion: promotion,
                            })];
                }
            });
        });
    };
    Visit.prototype.removePromotion = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.delete(BASE_URL + "/" + this.data.id + "/promotion")];
                    case 1:
                        _a.sent();
                        return [2, this.createSelf({
                                promotion: null,
                            })];
                }
            });
        });
    };
    Visit.prototype.postPayment = function (token) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post(BASE_URL + "/" + this.data.id + "/payment", {
                            token: token,
                        })];
                    case 1:
                        _a.sent();
                        return [2, this.completeTask(TaskCodes.PAYMENT)];
                }
            });
        });
    };
    Visit.prototype.completeVideoTask = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2, this.completeTask(TaskCodes.VIDEO)];
            });
        });
    };
    Visit.prototype.cancelVisit = function () {
        return this.client.http.delete(BASE_URL + "/" + this.data.id);
    };
    Visit.prototype.getReceiptUrl = function () {
        return __awaiter(this, void 0, void 0, function () {
            var receiptUrl;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.get(BASE_URL + "/" + this.data.id + "/receipt")];
                    case 1:
                        receiptUrl = (_a.sent()).data.data.receiptUrl;
                        return [2, receiptUrl];
                }
            });
        });
    };
    Visit.prototype.giveConsent = function () {
        return this.client.http.post(BASE_URL + "/" + this.data.id + "/consent");
    };
    return Visit;
}(Model));
export default Visit;
//# sourceMappingURL=visit.js.map